/** Level-label artwork and the VIP badge for the jackpot statistics drawer. */

import { JACKPOT_NAMES } from '@repo/common';
import { JACKPOT_STATISTICS_LEVEL_KEYS } from './constants';
import { normalizeJackpotLevelName, normalizeJackpotName, resolveJackpotLevelKey } from './utils/jackpotStatistics';

const STATISTICS_ASSETS_ROOT = '/statistics';

/**
 * The gold "VIP" wordmark rendered immediately before the level wordmark for premium-section
 * wins — e.g. `VIP GRAND` — in the header caption line and in every PAYOUT cell.
 */
export const VIP_BADGE_ASSET = `${STATISTICS_ASSETS_ROOT}/Vip_1x`;

/** The fireworks illustration of the drawer's "nobody has won yet" state (an SVG). */
export const FIREWORKS_ASSET = `${STATISTICS_ASSETS_ROOT}/Fireworks`;

/**
 * Instance names of the premium jackpot families, taken from the shared `JACKPOT_NAMES`
 * constant rather than an ad-hoc "does the name start with VIP" test, so a rename in
 * @repo/common propagates here.
 */
const VIP_JACKPOT_NAMES: readonly string[] = [
  JACKPOT_NAMES.VIP_BELL_LINK,
  JACKPOT_NAMES.VIP_BELL_LINK_BOOST,
  JACKPOT_NAMES.VIP_HIGH_CASH,
  JACKPOT_NAMES.VIP_GODS_AND_KINGS,
];

/** True for the premium families, which show the VIP badge before every level wordmark. */
export const isVipJackpot = (jackpotName: string): boolean => {
  const normalized = normalizeJackpotName(jackpotName);
  return VIP_JACKPOT_NAMES.some((name) => name.toLowerCase() === normalized);
};

/** Jackpot families that share one set of level wordmarks. */
const JACKPOT_FAMILIES = {
  BELL_LINK: 'bell_link',
  GODS_AND_KINGS: 'gods_and_kings',
  HIGH_CASH: 'high_cash',
  CLOVER_CHANCE: 'clover_chance',
  PREMIUM_LINK: 'premium_link',
} as const;

type JackpotFamily = (typeof JACKPOT_FAMILIES)[keyof typeof JACKPOT_FAMILIES];

/**
 * (family, level key) -> wordmark path. VIP and Boost jackpot NAMES deliberately resolve to
 * their base family: the artwork is the same word, and premium-ness is conveyed by the separate
 * VIP badge. Boost LEVELS need no key here either — they are merged into their base level before
 * anything is rendered, so they arrive at this map as `Grand` / `Major`.
 */
const LEVEL_LABEL_ASSETS: Record<JackpotFamily, Record<string, string>> = {
  [JACKPOT_FAMILIES.BELL_LINK]: {
    [JACKPOT_STATISTICS_LEVEL_KEYS.GRAND]: `${STATISTICS_ASSETS_ROOT}/BellLinkGrandLabel_1x`,
    [JACKPOT_STATISTICS_LEVEL_KEYS.MAJOR]: `${STATISTICS_ASSETS_ROOT}/BellLinkMajorLabel_1x`,
  },
  [JACKPOT_FAMILIES.GODS_AND_KINGS]: {
    [JACKPOT_STATISTICS_LEVEL_KEYS.GRAND]: `${STATISTICS_ASSETS_ROOT}/G_K_GrandLabel_1x`,
    [JACKPOT_STATISTICS_LEVEL_KEYS.MAJOR]: `${STATISTICS_ASSETS_ROOT}/G_K_MajorLabel_1x`,
  },
  [JACKPOT_FAMILIES.HIGH_CASH]: {
    [JACKPOT_STATISTICS_LEVEL_KEYS.GRAND]: `${STATISTICS_ASSETS_ROOT}/HIghCashGrandLabel_1x`,
    [JACKPOT_STATISTICS_LEVEL_KEYS.MAJOR]: `${STATISTICS_ASSETS_ROOT}/HIghCashMajorLabel_1x`,
  },
  [JACKPOT_FAMILIES.CLOVER_CHANCE]: {
    [JACKPOT_STATISTICS_LEVEL_KEYS.GRAND]: `${STATISTICS_ASSETS_ROOT}/CloverChanceGrandLabel_1x`,
    [JACKPOT_STATISTICS_LEVEL_KEYS.MAJOR]: `${STATISTICS_ASSETS_ROOT}/CloverChanceMajorLabel_1x`,
    [JACKPOT_STATISTICS_LEVEL_KEYS.MINOR]: `${STATISTICS_ASSETS_ROOT}/CloverChanceMinorLabel_1x`,
    [JACKPOT_STATISTICS_LEVEL_KEYS.MINI]: `${STATISTICS_ASSETS_ROOT}/CloverChanceMiniLabel_1x`,
  },
  [JACKPOT_FAMILIES.PREMIUM_LINK]: {
    super: `${STATISTICS_ASSETS_ROOT}/PremiumLinkSuperLabel_1x`,
    middle: `${STATISTICS_ASSETS_ROOT}/PremiumLinkMiddleLabel_1x`,
    mini: `${STATISTICS_ASSETS_ROOT}/PremiumLinkMiniLabel_1x`,
  },
};

/**
 * Instance name -> family. Ordered most-specific-first so "VIP Bell Link Boost" cannot be
 * captured by the "bell link" rule before its own. VIP/Boost qualifiers collapse into the base
 * family on purpose (see LEVEL_LABEL_ASSETS).
 */
const FAMILY_RULES: ReadonlyArray<readonly [string, JackpotFamily]> = [
  ['bell link', JACKPOT_FAMILIES.BELL_LINK],
  ['gods and kings', JACKPOT_FAMILIES.GODS_AND_KINGS],
  ['high cash', JACKPOT_FAMILIES.HIGH_CASH],
  ['clover chance', JACKPOT_FAMILIES.CLOVER_CHANCE],
  ['premium link', JACKPOT_FAMILIES.PREMIUM_LINK],
];

const resolveFamily = (jackpotName: string): JackpotFamily | null => {
  const normalized = normalizeJackpotName(jackpotName)
    .replace(/\bvip\b/g, '')
    .replace(/\bboost\b/g, '')
    .replace(/[^a-z]+/g, ' ')
    .trim();

  return FAMILY_RULES.find(([pattern]) => normalized.includes(pattern))?.[1] ?? null;
};

/**
 * True only for the Premium Link family (its VIP / Boost variants included).
 *
 * Used by the header to skip the optical nudge applied to the other families: Premium Link’s
 * Super / Middle / Mini wordmarks sit differently against the caption and the amount than the
 * Grand / Major / Minor / Mini artwork every other family uses.
 */
export const isPremiumLinkJackpot = (jackpotName: string): boolean =>
  resolveFamily(jackpotName) === JACKPOT_FAMILIES.PREMIUM_LINK;

/**
 * Whether this jackpot has a statistics drawer at all — only the multi-tier families above (plus
 * their VIP / Boost variants). InstantLink and the single-progressive jackpots (Burning Hot,
 * 20/40 Super Hot, Rise of Ra, Versailles Gold and friends) have no statistics.
 */
export const hasJackpotStatistics = (jackpotName: string): boolean => resolveFamily(jackpotName) !== null;

/**
 * Path of the level wordmark, or `null` when no artwork exists for that (family, level) pair —
 * callers must render the styled text label instead.
 *
 * @param levelName the API's level name (`Grand`, `BoostMajor`, …), not a level id.
 */
const NO_LEVEL_KEYS: readonly string[] = [];

/**
 * Premium Link's three tiers are drawn in the panel's first three rows: SUPER in the `grand`
 * slot, MIDDLE in `major`, MINI in `minor` (see the PremiumLink variant's icon map). Every
 * other family's wordmark keys ARE its panel row keys.
 */
const PREMIUM_LINK_PANEL_ROWS: readonly string[] = [
  JACKPOT_STATISTICS_LEVEL_KEYS.GRAND,
  JACKPOT_STATISTICS_LEVEL_KEYS.MAJOR,
  JACKPOT_STATISTICS_LEVEL_KEYS.MINOR,
];

/**
 * The PANEL row keys (`grand` / `major` / `minor` / `mini`) this jackpot has statistics for —
 * the rows that open the drawer. Read off the wordmark map above, which is the design's own
 * statement of each family's tiers and matches the win-stats endpoint: Bell Link, Gods and Kings
 * and High Cash report GRAND and MAJOR only, Clover Chance all four, Premium Link its three. A
 * family's other panel rows (High Cash MINOR / MINI) stay inert — there is no history behind
 * them to show. Empty for any jackpot without statistics, InstantLink included.
 */
export const getJackpotStatisticsLevelKeys = (jackpotName: string): readonly string[] => {
  const family = resolveFamily(jackpotName);
  if (!family) return NO_LEVEL_KEYS;
  return family === JACKPOT_FAMILIES.PREMIUM_LINK ? PREMIUM_LINK_PANEL_ROWS : Object.keys(LEVEL_LABEL_ASSETS[family]);
};

export const getJackpotLevelLabelAsset = (jackpotName: string, levelName: string): string | null => {
  const family = resolveFamily(jackpotName);
  if (!family) return null;
  // Canonical key first (Grand/Major/Minor/Mini), then the raw name — that second lookup is what
  // resolves Premium Link's Super / Middle / Mini, which have no canonical key of their own.
  const byKey = resolveJackpotLevelKey(levelName);
  const assets = LEVEL_LABEL_ASSETS[family];
  return (byKey ? assets[byKey] : undefined) ?? assets[normalizeJackpotLevelName(levelName)] ?? null;
};
